import { MultiStepGoalWidget } from "@/Components/MultiStepGoalWidget/MultiStepGoalWidget";
import { SimpleDonoGoalWidget } from "@/Components/SimpleDonoGoalWidget/SimpleDonoGoalWidget";
import { useSocket } from "@/hooks/useSocket";
import { donationIcons } from "@/lib/utils";
import {
    AnyGoal,
    AnyGoalDoc,
    CreateGoalPayload,
    NewMultiStepGoalStep,
} from "@ailenax-overlay/shared";
import { FC, ReactNode, useCallback, useMemo, useState } from "react";
import { FaRegGem } from "react-icons/fa";
import { GrSteps } from "react-icons/gr";
import { RiGeminiLine, RiGift2Fill } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

type GoalKind = "donation" | "bits" | "subs";
type GoalTypeChoice = "simple" | "multistep";

const defaultSimpleTarget = (kind: GoalKind) => (kind === "bits" ? 1000 : 100);

const defaultMultistepSteps = (kind: GoalKind): NewMultiStepGoalStep[] => {
    const base = defaultSimpleTarget(kind);
    return [
        { title: "Step 1", goal: base },
        { title: "Step 2", goal: base * 2.5 },
    ];
};

const buildPayload = (
    kind: GoalKind,
    goalType: GoalTypeChoice,
    simpleTitle: string,
    simpleGoal: number,
    steps: NewMultiStepGoalStep[],
    enabled: boolean,
    autoReset: boolean,
    wrapAround: boolean,
    shared: boolean,
    resetOnSessionStart: boolean
): CreateGoalPayload => {
    const base = { enabled, progress: 0, resetOnSessionStart };

    if (goalType === "simple") {
        const simple = {
            ...base,
            goalType: "simple" as const,
            kind,
            title: simpleTitle,
            goal: simpleGoal,
            autoReset,
        };
        if (kind === "donation") return simple;
        return { ...simple, shared };
    }

    const multistep = {
        ...base,
        goalType: "multistep" as const,
        kind,
        steps,
        wrapAround,
    };
    if (kind === "donation") return multistep;
    return { ...multistep, shared };
};

export const NewGoal: FC = () => {
    const socket = useSocket();
    const navigate = useNavigate();

    const [kind, setKind] = useState<GoalKind>("donation");
    const [goalType, setGoalType] = useState<GoalTypeChoice>("simple");
    const [simpleTitle, setSimpleTitle] = useState("New Goal");
    const [simpleGoal, setSimpleGoal] = useState(100);
    const [steps, setSteps] = useState<NewMultiStepGoalStep[]>(defaultMultistepSteps("donation"));
    const [enabled, setEnabled] = useState(true);
    const [autoReset, setAutoReset] = useState(false);
    const [wrapAround, setWrapAround] = useState(false);
    const [shared, setShared] = useState(false);
    const [resetOnSessionStart, setResetOnSessionStart] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);

    const onKindChange = (next: GoalKind) => {
        setKind(next);
        setSimpleGoal(defaultSimpleTarget(next));
        setSteps(defaultMultistepSteps(next));
    };

    const previewGoal = useMemo((): AnyGoal | null => {
        try {
            return buildPayload(
                kind,
                goalType,
                simpleTitle,
                simpleGoal,
                steps,
                enabled,
                autoReset,
                wrapAround,
                shared,
                resetOnSessionStart
            );
        } catch {
            return null;
        }
    }, [
        kind,
        goalType,
        simpleTitle,
        simpleGoal,
        steps,
        enabled,
        autoReset,
        wrapAround,
        shared,
        resetOnSessionStart,
    ]);

    const updateStep = (index: number, field: keyof NewMultiStepGoalStep, value: string | number) => {
        setSteps((prev) => prev.map((step, i) => (i === index ? { ...step, [field]: value } : step)));
    };

    const addStep = () => {
        setSteps((prev) => {
            const lastGoal = prev.at(-1)?.goal ?? defaultSimpleTarget(kind);
            return [...prev, { title: `Step ${prev.length + 1}`, goal: lastGoal + defaultSimpleTarget(kind) }];
        });
    };

    const removeStep = (index: number) => {
        setSteps((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    };

    const handleCreate = useCallback(() => {
        const payload = buildPayload(
            kind,
            goalType,
            simpleTitle,
            simpleGoal,
            steps,
            enabled,
            autoReset,
            wrapAround,
            shared,
            resetOnSessionStart
        );

        setCreating(true);
        setError(null);

        socket.emit("create-new-goal", payload, (created) => {
            setCreating(false);
            if (created?._id) {
                navigate(`/goals/${created._id}`);
            } else {
                setError("Could not create goal. Check your values and try again.");
            }
        });
    }, [
        socket,
        navigate,
        kind,
        goalType,
        simpleTitle,
        simpleGoal,
        steps,
        enabled,
        autoReset,
        wrapAround,
        shared,
        resetOnSessionStart,
    ]);

    const kindOptions: { id: GoalKind; label: string; icon: ReactNode }[] = [
        {
            id: "donation",
            label: "Donation",
            icon: donationIcons[import.meta.env.VITE_DONATION_CURRENCY],
        },
        { id: "bits", label: "Bits", icon: <FaRegGem className="inline-block" /> },
        { id: "subs", label: "Subs", icon: <RiGift2Fill className="inline-block" /> },
    ];

    return (
        <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">New Goal</h1>
            </div>

            <div className="col-span-12">
                <h2 className="text-sm font-medium text-gray-400 mb-4 text-center">Preview</h2>
                <div className="min-w-3xl max-w-max m-auto mb-8">
                    {previewGoal?.goalType === "simple" && (
                        <SimpleDonoGoalWidget
                            goal={{ ...previewGoal, _id: "preview" } as AnyGoalDoc & { goalType: "simple" }}
                        />
                    )}
                    {previewGoal?.goalType === "multistep" && (
                        <MultiStepGoalWidget
                            goal={{ ...previewGoal, _id: "preview" } as AnyGoalDoc & { goalType: "multistep" }}
                        />
                    )}
                </div>
            </div>

            <div className="col-span-12">
                <form className="flex flex-col max-w-2xl mx-auto dark:bg-gray-800 p-5 rounded-3xl">
                    <h2 className="text-sm font-medium text-gray-400 mb-4">Goal kind</h2>
                    <div className="flex flex-wrap gap-3 mb-6">
                        {kindOptions.map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => onKindChange(opt.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                                    kind === opt.id
                                        ? "bg-violet-600 text-white"
                                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                }`}
                            >
                                {opt.icon}
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    <h2 className="text-sm font-medium text-gray-400 mb-4">Goal type</h2>
                    <div className="flex flex-wrap gap-3 mb-6">
                        <button
                            type="button"
                            onClick={() => setGoalType("simple")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                                goalType === "simple"
                                    ? "bg-violet-600 text-white"
                                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                            }`}
                        >
                            <RiGeminiLine />
                            Simple
                        </button>
                        <button
                            type="button"
                            onClick={() => setGoalType("multistep")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                                goalType === "multistep"
                                    ? "bg-violet-600 text-white"
                                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                            }`}
                        >
                            <GrSteps />
                            Multistep
                        </button>
                    </div>

                    <div className="flex flex-wrap gap-6 mb-6">
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                            <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => setEnabled(e.target.checked)}
                                className="w-4 h-4 rounded"
                            />
                            Enabled
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                            <input
                                type="checkbox"
                                checked={resetOnSessionStart}
                                onChange={(e) => setResetOnSessionStart(e.target.checked)}
                                className="w-4 h-4 rounded"
                            />
                            Reset on Session-Start
                        </label>
                        {(kind === "bits" || kind === "subs") && (
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={shared}
                                    onChange={(e) => setShared(e.target.checked)}
                                    className="w-4 h-4 rounded"
                                />
                                Shared
                            </label>
                        )}
                    </div>

                    {goalType === "simple" ? (
                        <>
                            <div className="relative z-0 mb-5 group">
                                <input
                                    type="text"
                                    className="block py-2.5 px-0 w-full text-sm text-white bg-transparent border-0 border-b-2 border-gray-600 focus:border-blue-500 focus:outline-none peer"
                                    value={simpleTitle}
                                    onChange={(e) => setSimpleTitle(e.target.value)}
                                    required
                                />
                                <label className="text-sm text-gray-400">Title</label>
                            </div>
                            <div className="relative z-0 mb-5 group">
                                <input
                                    type="number"
                                    className="block py-2.5 px-0 w-full text-sm text-white bg-transparent border-0 border-b-2 border-gray-600 focus:border-blue-500 focus:outline-none peer"
                                    value={simpleGoal}
                                    onChange={(e) => setSimpleGoal(parseFloat(e.target.value) || 0)}
                                    step="0.01"
                                    required
                                />
                                <label className="text-sm text-gray-400">Target</label>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-gray-300 mb-6">
                                <input
                                    type="checkbox"
                                    checked={autoReset}
                                    onChange={(e) => setAutoReset(e.target.checked)}
                                    className="w-4 h-4 rounded"
                                />
                                Auto-Reset
                            </label>
                        </>
                    ) : (
                        <>
                            <label className="flex items-center gap-2 text-sm text-gray-300 mb-4">
                                <input
                                    type="checkbox"
                                    checked={wrapAround}
                                    onChange={(e) => setWrapAround(e.target.checked)}
                                    className="w-4 h-4 rounded"
                                />
                                Wrap Around
                            </label>
                            <div className="flex flex-col gap-4 mb-4">
                                {steps.map((step, index) => (
                                    <div key={index} className="flex gap-3 items-end flex-wrap">
                                        <div className="flex-1 min-w-[10rem]">
                                            <input
                                                type="text"
                                                className="block py-2 w-full text-sm text-white bg-transparent border-b border-gray-600 focus:border-blue-500 focus:outline-none"
                                                value={step.title}
                                                onChange={(e) => updateStep(index, "title", e.target.value)}
                                            />
                                            <span className="text-xs text-gray-500">Step {index + 1} title</span>
                                        </div>
                                        <div className="w-28">
                                            <input
                                                type="number"
                                                className="block py-2 w-full text-sm text-white bg-transparent border-b border-gray-600 focus:border-blue-500 focus:outline-none"
                                                value={step.goal}
                                                onChange={(e) =>
                                                    updateStep(index, "goal", parseFloat(e.target.value) || 0)
                                                }
                                                step="0.01"
                                            />
                                            <span className="text-xs text-gray-500">Threshold</span>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={steps.length <= 1}
                                            className="text-red-400 text-sm disabled:opacity-30"
                                            onClick={() => removeStep(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                className="mb-6 text-sm text-gray-300 hover:text-white"
                                onClick={addStep}
                            >
                                + Add step
                            </button>
                        </>
                    )}

                    {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

                    <button
                        type="button"
                        disabled={creating}
                        className="text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50 font-medium rounded-lg text-sm px-5 py-2.5"
                        onClick={handleCreate}
                    >
                        {creating ? "Creating…" : "Create goal"}
                    </button>
                </form>
            </div>
        </div>
    );
};
